import React from 'react';
import { MapPin, Navigation, Clock, Route } from 'lucide-react';
import { Card } from '../ui/Card';

/**
 * RouteDisplay Component
 * 
 * A component for displaying trip route information with pickup and dropoff locations,
 * including visual indicators and distance/duration information.
 */
export function RouteDisplay({ 
  pickup, 
  dropoff, 
  distance, 
  duration, 
  estimatedDuration,
  showMap = false,
  className = '' 
}) {
  const formatDuration = (minutes) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Route Points */}
      <div className="space-y-3">
        {/* Pickup Location */}
        <div className="flex items-start space-x-3">
          <div className="w-4 h-4 rounded-full bg-green-500 mt-1 flex-shrink-0 ring-2 ring-green-200"></div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-1">
              <MapPin className="w-4 h-4 text-green-600" />
              <span className="text-xs font-medium text-green-700 uppercase tracking-wide">
                Pickup
              </span>
            </div>
            <p className="text-sm font-medium text-gray-900 truncate">
              {pickup.name}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {pickup.address}
            </p>
            {pickup.coordinates && (
              <p className="text-xs text-gray-400 mt-1">
                {pickup.coordinates.lat.toFixed(4)}, {pickup.coordinates.lng.toFixed(4)}
              </p>
            )}
          </div>
        </div>

        {/* Route Line */}
        <div className="flex items-center space-x-3 ml-2">
          <div className="w-px h-6 bg-gray-300"></div>
          <div className="flex items-center space-x-4 text-xs text-gray-500">
            {distance && (
              <div className="flex items-center space-x-1">
                <Route className="w-3 h-3" />
                <span>{distance} km</span>
              </div>
            )}
            {(duration || estimatedDuration) && (
              <div className="flex items-center space-x-1">
                <Clock className="w-3 h-3" />
                <span>
                  {duration ? formatDuration(duration) : formatDuration(estimatedDuration)}
                  {duration && estimatedDuration && duration !== estimatedDuration && (
                    <span className="text-gray-400 ml-1">
                      (est. {formatDuration(estimatedDuration)})
                    </span>
                  )}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Dropoff Location */}
        <div className="flex items-start space-x-3">
          <div className="w-4 h-4 rounded-full bg-red-500 mt-1 flex-shrink-0 ring-2 ring-red-200"></div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-1">
              <MapPin className="w-4 h-4 text-red-600" />
              <span className="text-xs font-medium text-red-700 uppercase tracking-wide">
                Dropoff
              </span>
            </div>
            <p className="text-sm font-medium text-gray-900 truncate">
              {dropoff.name}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {dropoff.address}
            </p>
            {dropoff.coordinates && (
              <p className="text-xs text-gray-400 mt-1">
                {dropoff.coordinates.lat.toFixed(4)}, {dropoff.coordinates.lng.toFixed(4)}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Map Placeholder */}
      {showMap && (
        <div className="bg-gray-100 rounded-lg p-8 text-center">
          <Navigation className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Map view would be displayed here</p>
          <p className="text-xs text-gray-400 mt-1">
            Integration with mapping service required
          </p>
        </div>
      )}
    </div>
  );
}


export function RouteCard({ 
  pickup, 
  dropoff, 
  distance, 
  duration, 
  estimatedDuration,
  title = "Route Details",
  showMap = false,
  className = '' 
}) {
  return (
    <Card className={`p-6 ${className}`}>
      <div className="flex items-center space-x-2 mb-4">
        <Navigation className="w-5 h-5 text-blue-600" />
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      </div>
      
      <RouteDisplay
        pickup={pickup}
        dropoff={dropoff}
        distance={distance}
        duration={duration}
        estimatedDuration={estimatedDuration}
        showMap={showMap}
      />
    </Card>
  );
}


export function CompactRouteDisplay({ 
  pickup, 
  dropoff, 
  distance, 
  className = '' 
}) {
  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <div className="flex items-center space-x-1">
        <div className="w-2 h-2 rounded-full bg-green-500"></div>
        <span className="text-sm text-gray-900 truncate max-w-24">
          {pickup.name}
        </span>
      </div>
      
      <div className="flex items-center space-x-1 text-gray-400">
        <div className="w-4 h-px bg-gray-300"></div>
        <Navigation className="w-3 h-3" />
        <div className="w-4 h-px bg-gray-300"></div>
      </div>
      
      <div className="flex items-center space-x-1">
        <div className="w-2 h-2 rounded-full bg-red-500"></div>
        <span className="text-sm text-gray-900 truncate max-w-24">
          {dropoff.name}
        </span>
      </div>
      
      {distance && (
        <div className="text-xs text-gray-500 ml-2">
          {distance} km
        </div>
      )}
    </div>
  );
}

/**
 * RouteProgress Component
 * 
 * Shows route progress for ongoing trips
 */
export function RouteProgress({ 
  pickup, 
  dropoff, 
  currentLocation,
  progress = 0, // 0-100 percentage
  className = '' 
}) {
  return (
    <div className={`space-y-3 ${className}`}>
      {/* Progress Bar */}
      <div className="relative">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-700">Trip Progress</span>
          <span className="text-xs text-gray-500">{Math.round(progress)}%</span>
        </div>
        
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>

      {/* Route with Current Location */}
      <div className="space-y-2">
        <div className="flex items-center space-x-3">
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
          <span className="text-sm text-gray-900">{pickup.name}</span>
        </div>
        
        {currentLocation && (
          <div className="flex items-center space-x-3 ml-1">
            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
            <span className="text-sm text-blue-600 font-medium">Current Location</span>
          </div>
        )}
        
        <div className="flex items-center space-x-3">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <span className="text-sm text-gray-900">{dropoff.name}</span>
        </div>
      </div>
    </div>
  );
}
